import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Upload, Trash2, Archive, Download, RotateCcw, Receipt, ListChecks, FileSpreadsheet } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { LulControllo, DoppioniBuste } from "./BusteLul";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const now = new Date();
const BOXES = [
  { tipo: "buste", label: "Buste paga", icon: FileText },
  { tipo: "netti", label: "Elenco dei netti", icon: ListChecks },
  { tipo: "f24", label: "F24 da pagare (il 16)", icon: FileSpreadsheet },
  { tipo: "ricevuta", label: "Ricevuta", icon: Receipt },
];
const API = process.env.REACT_APP_BACKEND_URL;

function Box({ mese, box, files, onChanged, onLul }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const Icon = box.icon;

  const upload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/buste-doc/${mese}/${box.tipo}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (box.tipo === "buste" && /\.pdf$/i.test(f.name)) {
          try {
            const { data: imp } = await api.post(`/buste-lul/analizza/${data.id}`);
            if (imp.id) onLul(imp.id); else toast.message(`${f.name}: non ho trovato buste di dipendenti, resta solo come documento`);
          } catch (e) { toast.error(e.response?.data?.detail || "Non riesco a leggere le buste dal PDF"); }
        }
      }
      toast.success(`${box.label}: caricato`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel caricamento"); }
    setBusy(false);
  };
  const del = async (id) => {
    try { await api.delete(`/buste-doc/${id}`); toast.success("Spostato in archivio"); onChanged(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div
      className={`rounded-md border p-1.5 flex flex-col gap-1 transition-colors ${drag ? "border-primary bg-primary/5" : "border-border bg-secondary/20"}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
      data-testid={`bustedoc-box-${box.tipo}`}
    >
      <div className="flex items-center gap-1 text-[11px] font-semibold">
        <Icon size={12} className="text-primary shrink-0" />
        <span className="truncate">{box.label}</span>
      </div>
      <input ref={ref} type="file" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid={`bustedoc-input-${box.tipo}`} />
      <button
        type="button" onClick={() => ref.current?.click()} disabled={busy}
        className="text-[10px] text-muted-foreground border border-dashed rounded py-1.5 px-1 hover:border-primary hover:text-primary transition-colors leading-tight"
        data-testid={`bustedoc-drop-${box.tipo}`}
      >
        <Upload size={11} className="inline mr-0.5" />{busy ? "Carico…" : "Carica"}
      </button>
      <div className="flex flex-col gap-0.5 max-h-20 overflow-y-auto">
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-1 text-[10px] bg-white rounded px-1 py-0.5 border" data-testid={`bustedoc-file-${f.id}`}>
            <a href={`${API}/api/buste-doc/file/${f.id}`} target="_blank" rel="noreferrer" className="truncate flex-1 text-primary hover:underline" title={f.nome}>{f.nome}</a>
            {f.lul && <button onClick={() => onLul(f.lul.id)} className={`shrink-0 rounded px-1 font-bold ${f.lul.consegnate < f.lul.totale || f.lul.doppioni ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"}`} title="Controllo della consegna agli operatori" data-testid={`bustedoc-lul-${f.id}`}>{f.lul.consegnate}/{f.lul.totale}</button>}
            <button onClick={() => del(f.id)} className="text-destructive shrink-0" title="Elimina (va in archivio)" data-testid={`bustedoc-del-${f.id}`}><Trash2 size={11} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BusteDocumenti({ meseFisso }) {
  const qc = useQueryClient();
  const [annoState, setAnnoState] = useState(now.getFullYear());
  const [meseState, setMeseState] = useState(now.getMonth() + 1);
  const [archOpen, setArchOpen] = useState(false);
  const [lulId, setLulId] = useState(null);
  const anno = meseFisso ? Number(meseFisso.split("-")[0]) : annoState;
  const mese = meseFisso ? Number(meseFisso.split("-")[1]) : meseState;
  const setAnno = setAnnoState;
  const setMese = setMeseState;
  const meseKey = meseFisso || `${anno}-${String(mese).padStart(2, "0")}`;

  const { data: docs = { buste: [], netti: [], f24: [], ricevuta: [] } } = useQuery({
    queryKey: ["buste-doc", meseKey],
    queryFn: async () => (await api.get(`/buste-doc/${meseKey}`)).data,
  });
  const { data: arch = [], refetch: refetchArch } = useQuery({
    queryKey: ["buste-doc-arch", meseKey],
    queryFn: async () => (await api.get(`/buste-doc/${meseKey}/archivio`)).data,
    enabled: archOpen,
  });
  const onChanged = () => { qc.invalidateQueries({ queryKey: ["buste-doc", meseKey] }); qc.invalidateQueries({ queryKey: ["buste"] }); qc.invalidateQueries({ queryKey: ["buste-doppioni"] }); if (archOpen) refetchArch(); };
  const restore = async (id) => { try { await api.post(`/buste-doc-restore/${id}`); toast.success("Ripristinato"); onChanged(); } catch { toast.error("Errore"); } };

  const tipoLabel = (t) => BOXES.find((b) => b.tipo === t)?.label || t;

  return (
    <div className="gr-card p-2.5 w-full lg:max-w-2xl" data-testid="buste-documenti-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-head font-bold">Documenti del consulente{meseFisso ? ` · ${MESI[mese - 1]} ${anno}` : ""}</div>
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={() => setArchOpen(true)} data-testid="bustedoc-archivio-btn"><Archive size={12} className="mr-1" /> Archivio</Button>
      </div>
      {!meseFisso && (
        <div className="flex items-center gap-2 mb-2.5">
          <select value={mese} onChange={(e) => setMese(Number(e.target.value))} className="text-xs border rounded px-2 py-1 bg-background" data-testid="bustedoc-mese">
            {MESI.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={anno} onChange={(e) => setAnno(Number(e.target.value))} className="text-xs border rounded px-2 py-1 bg-background" data-testid="bustedoc-anno">
            {[now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {BOXES.map((b) => <Box key={b.tipo} mese={meseKey} box={b} files={docs[b.tipo] || []} onChanged={onChanged} onLul={(id) => { setLulId(id); onChanged(); }} />)}
      </div>
      <DoppioniBuste mese={meseKey} />
      <p className="text-[10px] text-muted-foreground mt-1.5 leading-tight">In «Buste paga» puoi caricare il Libro Unico del consulente (PDF): ogni busta va da sola nella scheda dell'operatore giusto, nel mese giusto. La «Ricevuta» compare anche in F24 mensili. I file eliminati vanno in Archivio.</p>
      <LulControllo impId={lulId} onClose={() => setLulId(null)} onChanged={onChanged} />

      <Dialog open={archOpen} onOpenChange={setArchOpen}>
        <DialogContent data-testid="bustedoc-archivio-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Archive size={18} /> Archivio · {MESI[mese - 1]} {anno}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto flex flex-col gap-1.5">
            {arch.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Nessun file in archivio per questo mese.</p>}
            {arch.map((f) => (
              <div key={f.id} className="flex items-center gap-2 text-sm border rounded-md px-2.5 py-2" data-testid={`bustedoc-arch-${f.id}`}>
                <span className="text-[10px] font-semibold uppercase text-muted-foreground w-20 shrink-0">{tipoLabel(f.tipo)}</span>
                <span className="truncate flex-1" title={f.nome}>{f.nome}</span>
                <a href={`${API}/api/buste-doc/file/${f.id}`} target="_blank" rel="noreferrer" className="text-primary shrink-0" title="Scarica"><Download size={15} /></a>
                <button onClick={() => restore(f.id)} className="text-emerald-600 shrink-0" title="Ripristina" data-testid={`bustedoc-restore-${f.id}`}><RotateCcw size={15} /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
